import * as Complex from 'complex.js'
import TransferFunction from './TransferFunction'
import * as SVG from 'svg.js'

export default class BodePlot {
  draw: any
  tfunc: TransferFunction

  xMin: number
  yMin: number
  xMax: number
  yMax: number
  xRange: number
  yRange: number
  pad: number
  sr: number
  width: number
  height: number

  constructor(selector: string) {
    // TODO: don't hardcode sample rate.
    this.sr = 44100
    const el = document.querySelector(selector) as HTMLDivElement
    this.width = el.offsetWidth
    this.height = this.width * 0.75
    // Hack: if we're embedded, use the iframe body's height.
    if (window !== window.parent) {
      this.height = document.body.offsetHeight
    }
    this.draw = SVG(el).size(this.width, this.height)
  }

  setTransferFunction(tfunc: TransferFunction) {
    this.tfunc = tfunc

    // Calculate all values of |H(iw)|, for w in [0, pi].
    const w = range(0, Math.PI)
    const i = new Complex('i')
    const H_abs = w.map(w_i => tfunc.result(i.mul(w_i).exp()).abs())

    this.plot(logscale(w), logscale(H_abs))
    //this.plot(w, H_abs)
  }

  private plot(x: number[], y: number[]) {
    this.draw.clear()

    // Remove infinity terms
    for (let i = 0; i < x.length; i++) {
      if (Math.abs(x[i]) == Infinity || Math.abs(y[i]) == Infinity) {
        //console.log(`Found infinity at ${i}. Removing.`)
        x.splice(i, 1);
        y.splice(i, 1);
      }
    }
    y = y.map(y_i => -y_i)

    // Calculate extents of the plot.
    this.xMin = min(x)
    this.yMin = min(y)
    this.xMax = max(x)
    this.yMax = max(y)

    this.xRange = this.xMax - this.xMin
    this.yRange = this.yMax - this.yMin
    //this.draw.viewbox({x: this.xMin - this.xPad, y: this.yMin - this.yPad,
    //  width: this.xRange + 2*this.xPad, height: this.yRange + 2*this.yPad})
    this.pad = 40
    this.draw.viewbox({x: 0 - this.pad, y: 0 - this.pad,
      width: this.width + 2*this.pad, height: this.height + 2*this.pad})

    // Draw axes.
    this.createAxes()

    // Crucially, make this plot change aspect ratio.
    //this.draw.node.setAttribute('preserveAspectRatio', 'none')

    // Normalize to coordinates.
    const pairs = x.map((_, i) => [this.x(x[i]), this.y(y[i])])
    this.draw.polyline(pairs).stroke({width: 3}).fill('transparent')
  }

  private createAxes() {
    // Draw axes w/ ticks and labels.
    const GUIDE_DASH = [1, 5]
    const TICK_DASH = [3, 5]
    const TEXT_PAD = 4

    // X-axis.
    this.draw.line(this.x(this.xMin) - this.pad, this.y(this.yMax),
      this.x(this.xMax) + 2*this.pad, this.y(this.yMax))
      .stroke({width: 1})

    // Dotted guides.
    const dots = [1.25, 2.5, 5, 7.5, 8.75]
    const mags = [2, 3, 4]
    let allDots = []
    for (let mag of mags) {
      for (let dot of dots) {
        allDots.push(dot * Math.pow(10, mag))
      }
    }
    for (let f of allDots) {
      let w = (f / this.sr) * Math.PI
      let x = 20 * Math.log10(w)
      this.draw.line(this.x(x), this.y(this.yMin), this.x(x), this.y(this.yMax))
        .stroke({width: 0.5, dasharray: GUIDE_DASH})
    }

    for (let fexp of mags) {
      const freq = Math.pow(10, fexp)
      let percent = freq / this.sr
      let w = percent * Math.PI
      let x = 20 * Math.log10(w)
      this.draw.line(this.x(x), this.y(this.yMin), this.x(x), this.y(this.yMax))
        .stroke({width: 1, dasharray: TICK_DASH})

      let text = this.draw.text(`${freq} Hz`).move(this.x(x), this.y(this.yMax) + TEXT_PAD)
        .font({size: 12})
      text.native().setAttribute('text-anchor', 'middle')
    }

    // Y-axis:
    this.draw.line(this.x(this.xMin), this.y(this.yMin) - this.pad,
      this.x(this.xMin), this.y(this.yMax) + 2*this.pad)
      .stroke({width: 0.3})

    // Dotted guides.
    let dbs = []
    for (let i = -80; i < 20; i += 5) {
      dbs.push(i)
    }
    for (let db of dbs) {
      let y = -db
      this.draw.line(this.x(this.xMin), this.y(y), this.x(this.xMax), this.y(y))
        .stroke({width: 1, dasharray: TICK_DASH})

      let text = this.draw.text(`${db} dB`).move(this.x(this.xMin) - TEXT_PAD, this.y(y))
        .font({size: 12})
      text.native().setAttribute('text-anchor', 'end')
    }
  }

  x(val: number) {
    const out = (val - this.xMin) / this.xRange
    return (0 <= out && out <= 1) ? this.width * out : Infinity
  }

  y(val: number) {
    const out = (val - this.yMin) / this.yRange
    return (0 <= out && out <= 1) ? this.height * out : Infinity
  }
}

export function range(start, stop, n=1000) {
  const d = stop - start
  const step = Number(d) / Number(n)
  let out = []
  for (let i = 0; i < n; i++) {
    out.push(start + i*step)
  }
  return out
}

function min(array) {
  return Math.min.apply(this, array)
}

function max(array) {
  return Math.max.apply(this, array)
}

function logscale(array) {
  return array.map(x => 20 * Math.log10(x))
}



// WEBPACK FOOTER //
// ./src/BodePlot.ts